import type { Language } from '../../i18n/catalogs';

/**
 * Money crosses every API boundary as a decimal string (ADR-8). The UI never
 * turns it into a JavaScript number: a float cannot hold `0.1 + 0.2` exactly,
 * and accounting cannot afford the drift. Everything here works on the digit
 * string, and sums use BigInt on a shared scale.
 */

export type DecimalLanguage = Language;

export interface DecimalParts {
  negative: boolean;
  whole: string;
  fraction: string;
}

const DECIMAL = /^(-)?(\d+)(?:\.(\d+))?$/;

export function parseDecimal(value: string): DecimalParts {
  const match = DECIMAL.exec(value.trim());
  if (match === null) {
    throw new Error(`Not a decimal string: ${value}`);
  }
  return {
    negative: match[1] === '-',
    whole: match[2] ?? '0',
    fraction: match[3] ?? '',
  };
}

export function decimalScale(value: string): number {
  return parseDecimal(value).fraction.length;
}

/** Exact integer value at `scale` decimal places; a longer fraction is refused, not truncated. */
export function decimalToUnits(value: string, scale = 4): bigint {
  const parts = parseDecimal(value);
  if (parts.fraction.length > scale) {
    throw new Error(`Decimal ${value} has more than ${scale} fraction digits`);
  }
  const padded = parts.fraction.padEnd(scale, '0');
  const units = BigInt(`${parts.whole}${padded}`);
  return parts.negative ? -units : units;
}

export function unitsToDecimal(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? '' : `.${digits.slice(digits.length - scale)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

/** Sums decimal strings exactly, keeping the widest scale the inputs carry. */
export function sumDecimal(values: readonly string[]): string {
  const scale = values.reduce((widest, value) => Math.max(widest, decimalScale(value)), 0);
  let total = 0n;
  for (const value of values) {
    total += decimalToUnits(value, scale);
  }
  return unitsToDecimal(total, scale);
}

export function compareDecimal(left: string, right: string): -1 | 0 | 1 {
  const scale = Math.max(decimalScale(left), decimalScale(right));
  const a = decimalToUnits(left, scale);
  const b = decimalToUnits(right, scale);
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

function group(wholeDigits: string, language: DecimalLanguage): string {
  const separator = language === 'en' ? ',' : ' ';
  const digits = wholeDigits.replace(/^0+(?=\d)/, '');
  let grouped = '';
  for (let index = 0; index < digits.length; index += 1) {
    const positionFromEnd = digits.length - index;
    grouped += digits[index];
    if (positionFromEnd > 1 && positionFromEnd % 3 === 1) {
      grouped += separator;
    }
  }
  return grouped;
}

/** Rounds half away from zero to `decimals` places, then groups by locale. */
export function formatDecimal(
  value: string,
  language: DecimalLanguage,
  decimals: number,
): string {
  const parts = parseDecimal(value);
  const fraction = parts.fraction.padEnd(decimals + 1, '0');
  const kept = fraction.slice(0, decimals);
  const nextDigit = Number(fraction[decimals] ?? '0');
  let units = BigInt(`${parts.whole}${kept}` || '0');
  if (nextDigit >= 5) {
    units += 1n;
  }
  const digits = units.toString().padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  const tail = decimals === 0 ? '' : digits.slice(digits.length - decimals);
  const sign = parts.negative && units !== 0n ? '-' : '';
  const decimalSeparator = language === 'en' ? '.' : ',';
  return `${sign}${group(whole, language)}${decimals === 0 ? '' : `${decimalSeparator}${tail}`}`;
}

/** `1240` -> `1 240,00` (az/ru) or `1,240.00` (en). */
export function formatMoney(value: string, language: DecimalLanguage): string {
  return formatDecimal(value, language, 2);
}

/** Quantities carry four decimals (spec §3.2) and the same separators. */
export function formatQuantity(value: string, language: DecimalLanguage): string {
  return formatDecimal(value, language, 4);
}
